<template>
	<DataTable v-bind="$attrs" v-on="$listeners" :fields="fields" :sort-aliases="sortAliases" @sorted.once="sorted">
		<template v-slot:[`cell(id)`]="data">
			<div 
				v-if="data.value" 
				class="d-flex justify-content-between align-items-center text-muted" 
				:title="data.value"
			>
				<span>{{ data.value }}</span>
				<span v-if="data.item.request_type === 'MedicalRecord'">
				<span
					v-if="data.item.mr_status === '1'"
					class="circle-badge-green"
				>
					MR
				</span>
				<span
					v-else
					class="circle-badge"
				>
					MR
				</span>
				</span>
			</div>
			<div 
				v-else 
				class="text-uppercase font-weight-bold text-danger"
			>
				Missing
			</div>
		</template>

		<template v-slot:[`cell(request_status)`]="{ value, item}">
			<span v-if="value">
				<case-request-status-label :value="value" />
				<svg v-if="item.urgent" xmlns="http://www.w3.org/2000/svg" id="Layer_1" data-name="Layer 1" viewBox="0 0 24 24" width="23" height="16" fill="#ff5252">
				<path d="M13,2.051V1a1,1,0,0,0-2,0V2.051A10.98,10.98,0,0,0,3.681,20.176,3.024,3.024,0,0,0,2,23a1,1,0,0,0,2,0,1.2,1.2,0,0,1,1.11-1.229.932.932,0,0,0,.2-.062,10.924,10.924,0,0,0,13.39,0,1.033,1.033,0,0,0,.182.064A1.2,1.2,0,0,1,20,23a1,1,0,0,0,2,0,3.024,3.024,0,0,0-1.681-2.824A10.98,10.98,0,0,0,13,2.051ZM3,13a9,9,0,1,1,9,9A9.011,9.011,0,0,1,3,13Z"/>
				<path d="M19.215,0a1,1,0,0,0,0,2A2.59,2.59,0,0,1,22,4.5a1,1,0,0,0,2,0A4.6,4.6,0,0,0,19.215,0Z"/>
				<path d="M2,4.5A2.59,2.59,0,0,1,4.785,2a1,1,0,0,0,0-2A4.6,4.6,0,0,0,0,4.5a1,1,0,0,0,2,0Z"/>
				<path d="M13,11.586V7a1,1,0,0,0-2,0v5a1,1,0,0,0,.293.707l3,3a1,1,0,0,0,1.414-1.414Z"/>
				</svg>
				 <b-badge v-if="item.unable_to_complete" pill variant="warning" title="Unable To Complete">
                        <font-awesome-icon icon="exclamation-triangle" />
                 </b-badge>
            </span>
			
			<span v-else class="font-weight-bold text-uppercase text-danger">Missing</span>
		</template>

		<template v-slot:[`cell(name)`]="{ value }">
			<span v-if="value" class="fix-details" :title="value">{{ value }}</span>
			<span v-else class="font-weight-bold text-uppercase text-danger">
				<font-awesome-icon icon="exclamation-triangle" fixed-width />
				<span>Missing</span>
			</span>
		</template>

		<template v-slot:[`cell(case.patient.list_name)`]="{ value }">
			<div v-if="value" class="fix-width" :title="value">{{ value }}</div>
			<div v-else class="text-uppercase font-weight-bold text-danger fix-width">Missing</div>
		</template>

        <template v-slot:[`cell(case.facility.name)`]="{ value }">
			<span v-if="value" class="fix-width" :title="value" >{{ value}}</span>
			<span v-else class="text-muted fix-width">&mdash;</span>
		</template>



		<template v-slot:[`cell(type_label)`]="{ value }">
			<span :title="value" class="fix-width" >{{ value }}</span>
		</template>

		<template v-slot:[`cell(priority)`]="{ value }">
			<span v-if="value" class="text-success">
				<font-awesome-icon icon="check-circle" />
				<span class="sr-only">Priority</span>
			</span>
			<span v-else class="text-muted">&mdash;</span>
		</template>

		<template v-slot:[`cell(due_date)`]="{ value }">
			<span v-if="value">{{ $filters.formatDate(value) }}</span>
			<span v-else class="text-muted">&mdash;</span>
		</template>

		<template v-slot:[`cell(appeal.assigned_to_user.full_name)`]="{ value }">
			<span v-if="value" class="fix-width" :title="value">{{ value }}</span>
			<span v-else class="text-primary fix-width">Unassigned</span>
		</template>
        
		<template v-slot:[`cell(insurance_provider.name)`]="{ value }">
			<span v-if="value"  class="fix-width" :title="value">{{ value }}</span>
			<span v-else class="text-muted fix-width">&mdash;</span>
		</template>

		<template v-slot:[`cell(agency.name)`]="{ value }">
			<span v-if="value"  class="fix-width" :title="value">{{ value }}</span>
			<span v-else class="text-muted fix-width">&mdash;</span>
		</template>

		<template v-slot:[`cell(user_id)`]="{ item }">
			<span v-if="item?.submittedTo === 'User' && item?.user?.full_name" 
					class="fix-width" 
					:title="item.user.full_name">
				{{ item.user.full_name }}
			</span>
			<span v-else-if="item?.submittedTo === 'Facility'" 
					class="fix-width">
				Facility
			</span>
			<span v-else class="text-muted fix-width">
				&mdash;
			</span>
		</template>



		<template v-slot:[`cell(created)`]="{ value }">
			<span v-if="value">{{ $filters.formatTimestamp(value) }}</span>
			<span v-else class="text-muted">&mdash;</span>
		</template>

		<template v-slot:[`cell(modified)`]="{ value }">
			<span v-if="value">{{ $filters.formatTimestamp(value) }}</span>
			<span v-else class="text-muted">&mdash;</span>
		</template>
	</DataTable>
</template>

<script>
import { mapGetters } from "vuex";
import DataTable from "@/clients/components/Layout/data-table.vue";
import CaseRequestStatusLabel from "@/clients/components/CaseRequests/StatusLabel.vue";

export default {
	name: "CaseRequestTable",
	components: {
		CaseRequestStatusLabel,
		DataTable,
	},
	props: {
		hideAssignedTo: {
			type: Boolean,
			default: false,
		},
		hideStatus: {
			type: Boolean,
			default: false,
		},
		hidePatient: {
			type: Boolean,
			default: false,
		},
		sort: {
			type: String,
			default: "created",
		},
		sortDescending: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		fields() {
			return [
		     	{
					key: "id",
					label: "Request ID",
					sortable: true,
				},
				{
					key: "request_status",
					label: "Status",
					sortable: true,
				},
				{
					key: "name",
					label: "Details",
					sortable: true,
					isRowHeader: true,
				},
				{
					key: "case.patient.list_name",
					sortKey: "Patients.last_name",
					label: "Patient",
					sortable: true,
				},
				{
					key: "type_label",
					sortKey: "request_type",
					label: "Type",
					sortable: true,
				},
				{
					key: "due_date",
					label: "Due Date",
					sortable: true,
				},
				{
					key: "appeal.assigned_to_user.full_name",
					sortKey:"AssignedToUser.first_name",
					label: "Assign To",
					sortable: true,
				},
				{
					key: "user_id",
					label: "Sent To",
					// sortable: true,
				},
				{
					key: "user_id",
					label: "Sent To",
				},
				{
					key: "priority",
					label: "Priority",
					sortable: true,
				},
				{
					key: "case.facility.name",
					sortKey: "Facilities.name",
					label: "Facility",
					sortable: true,
				},
				{
					key: "insurance_provider.name",
					sortKey: "InsuranceProviders.name",
					label: "Insurance Provider",
					sortable: true,
				},
				{
					key: "agency.name",
					sortKey: "Agencies.name",
					label: "Agency",
					sortable: true,
				},
				{
					key: "created",
					label: "Created",
					sortable: true,
				},
				{
					key: "modified",
					label: "Updated",
					sortable: true,
				},
				
			];
		},
		...mapGetters({
			sortAliases: "caseRequests/sortAliases",
		}),
	},
	methods: {
		sorted(params) {
			
			this.$emit("update:sort", params.sort);
			this.$emit("update:sort-descending", params.sortDescending);
			this.$emit("sorted", params);
		},
	},
};
</script>
<style>
.circle-badge-green {
  display: inline-block;
  width: 24px;
  height: 16px;
  margin-left: 4px;
  line-height: 16px;
  border-radius: 50%;
  background-color: green;
  color: white;
  text-align: center;
  font-size: 12px;
}
.circle-badger {
  display: inline-block;
  width: 24px;
  height: 16px;
  margin-left: 4px;
  line-height: 16px;
  border-radius: 50%;
  background-color: #ff0000; 
  color: white;
  text-align: center;
  font-size: 12px;
}
.fix-width {
    width: 150px;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
.fix-details {
    width: 210px;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
}
</style>